import { useState } from "react";
import { Search, Info } from "lucide-react";
import { useLang, BRANDS } from "@/lib/i18n";
import { BrandLogo } from "./BrandLogo";
import { Reveal, Eyebrow } from "./Reveal";

const KEYS = Object.keys(BRANDS);
const norm = (s) => s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

const Group = ({ k, title, items }) => (
  <div data-testid={`brand-group-${k}`} className="border-t border-white/10 py-8 first:border-t-0">
    <div className="mb-5 flex items-baseline justify-between gap-4">
      <h3 className="font-display text-lg font-bold text-white sm:text-xl">{title}</h3>
      <span className="text-sm font-semibold text-slate-400">{items.length}</span>
    </div>
    <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8">
      {items.map((b) => (
        <span key={b} data-testid={`brand-chip-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="group flex cursor-default flex-col items-center justify-center gap-2 rounded-2xl bg-white/[0.04] px-3 py-4 text-center shadow-[0_6px_24px_-16px_rgba(0,0,0,.6)] ring-1 ring-white/10 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-16px_rgba(47,123,255,.55)]">
          <BrandLogo name={b} size="lg" />
          <span className="text-xs font-semibold leading-tight text-slate-300">{b}</span>
        </span>
      ))}
    </div>
  </div>
);

export const Brands = () => {
  const { t } = useLang();
  const b = t.brands;
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const shown = (tab === "all" ? KEYS : [tab])
    .map((k) => ({ k, items: BRANDS[k].filter((x) => norm(x).includes(norm(q))) }))
    .filter((g) => g.items.length);
  const tabCls = (active) => `rounded-full px-4 py-2 text-sm font-bold transition-colors ${active ? "bg-[#2F7BFF] text-white shadow-[0_8px_24px_-8px_rgba(47,123,255,.8)]" : "bg-white/5 text-white hover:bg-white/10"}`;

  return (
    <section id="brands" data-testid="brands-section" className="py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="grid gap-10 lg:grid-cols-2 lg:gap-20">
          <div>
            <Eyebrow>{b.eyebrow}</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-[44px] lg:leading-[1.1]">{b.title}</h2>
          </div>
          <div className="space-y-4 text-base leading-relaxed text-slate-300 lg:pt-10">
            <p>{b.p1}</p>
            <p>{b.p2}</p>
          </div>
        </Reveal>
        <Reveal delay={100} className="mt-14">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              <button data-testid="brand-tab-all" onClick={() => setTab("all")} className={tabCls(tab === "all")}>{b.all}</button>
              {KEYS.map((k) => (
                <button key={k} data-testid={`brand-tab-${k}`} onClick={() => setTab(k)} className={tabCls(tab === k)}>{b.tabs[k]}</button>
              ))}
            </div>
            <label className="relative block lg:w-72">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input data-testid="brand-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={b.search} className="w-full rounded-full border border-white/10 bg-white/[0.04] py-2.5 pl-11 pr-4 text-sm outline-none focus:border-[#2F7BFF] focus:ring-2 focus:ring-[#2F7BFF]/20" />
            </label>
          </div>
          <div className="mt-4">
            {shown.length ? shown.map((g) => <Group key={g.k} k={g.k} title={b.groups[g.k]} items={g.items} />) : (
              <p data-testid="brand-search-empty" className="py-10 text-center text-slate-400">—</p>
            )}
          </div>
        </Reveal>
        <p data-testid="brands-note" className="mt-6 flex items-start gap-2 text-sm text-slate-400"><Info className="mt-0.5 h-4 w-4 shrink-0" />{b.note}</p>
      </div>
    </section>
  );
};
